const SSO_LOGIN_ERROR_KEY = 'sso.login.error'
const SSO_RETRY_COUNT_KEY = 'sso.login.retries'

/** Consecutive silent Keycloak restarts before falling back to the form. */
const MAX_AUTO_RETRIES = 2

export interface SsoLoginError {
  message: string
  /**
   * A fresh Keycloak round-trip may heal this failure (a replayed one-shot
   * code, per-tab PKCE state that landed in the wrong tab, an OAuth `error=`
   * callback). The login page restarts the flow silently for these instead of
   * rendering the legacy local form.
   */
  retryable: boolean
}

export function rememberSsoLoginError(
  message: string,
  retryable = false,
): void {
  sessionStorage.setItem(
    SSO_LOGIN_ERROR_KEY,
    JSON.stringify({message, retryable}),
  )
}

export function consumeSsoLoginError(): SsoLoginError | null {
  const raw = sessionStorage.getItem(SSO_LOGIN_ERROR_KEY)
  sessionStorage.removeItem(SSO_LOGIN_ERROR_KEY)
  if (!raw) {
    return null
  }
  try {
    const parsed = JSON.parse(raw) as Partial<SsoLoginError>
    if (typeof parsed?.message === 'string') {
      return {message: parsed.message, retryable: parsed.retryable === true}
    }
  } catch {
    /* pre-structured format: the message was stored as a bare string */
  }
  return {message: raw, retryable: false}
}

/**
 * Claim one automatic-retry attempt. Returns `true` while the per-tab budget
 * lasts; once exhausted the caller must fall back to the local form so a
 * persistent Keycloak failure cannot redirect-loop.
 */
export function takeSsoRetryAttempt(): boolean {
  const used = Number(sessionStorage.getItem(SSO_RETRY_COUNT_KEY)) || 0
  if (used >= MAX_AUTO_RETRIES) {
    return false
  }
  sessionStorage.setItem(SSO_RETRY_COUNT_KEY, String(used + 1))
  return true
}

/** Refill the retry budget — call on a successful login or a manual attempt. */
export function resetSsoRetryAttempts(): void {
  sessionStorage.removeItem(SSO_RETRY_COUNT_KEY)
}
